import { useCallback, useEffect, useRef, useState } from 'react'
import { api, type Player, type NewPlayer, type UnknownPlayer } from '../api'

// XUID cache lives in localStorage keyed by gamertag.
// Populated when adding a player, editing via modal, or auto-filled by lookup.
const XUID_CACHE_KEY = 'mc_xuid_cache'

function getXuidCache(): Record<string, string> {
  try { return JSON.parse(localStorage.getItem(XUID_CACHE_KEY) ?? '{}') }
  catch { return {} }
}

function cacheXuid(gamertag: string, xuid: string) {
  const cache = getXuidCache()
  cache[gamertag] = xuid
  localStorage.setItem(XUID_CACHE_KEY, JSON.stringify(cache))
}

// Deterministic avatar color from gamertag
const AVATAR_COLORS = ['#3b82f6','#8b5cf6','#ec4899','#f59e0b','#14b8a6','#06b6d4','#f97316']
function avatarColor(gamertag: string) {
  let h = 0
  for (const c of gamertag) h = (h * 31 + c.charCodeAt(0)) & 0xffff
  return AVATAR_COLORS[h % AVATAR_COLORS.length]
}

function initials(name: string) {
  return name.split(' ').map(w => w[0]).join('').toUpperCase().slice(0, 2)
}

// ── Edit / Add player modal ──────────────────────────────────────────────────

interface ModalProps {
  player?:        Player        // if editing existing
  prefillGamertag?: string      // pre-filled from detect-new-players
  prefillXuid?:   string
  onSave:         (p: NewPlayer) => Promise<void>
  onClose:        () => void
  saving:         boolean
  saveError:      string | null
}

function PlayerModal({ player, prefillGamertag, prefillXuid, onSave, onClose, saving, saveError }: ModalProps) {
  const xuidCache = getXuidCache()
  const [gamertag,    setGamertag]    = useState(player?.gamertag    ?? prefillGamertag ?? '')
  const [displayName, setDisplayName] = useState(player?.displayName ?? '')
  const [xuid,        setXuid]        = useState(
    player ? (xuidCache[player.gamertag] ?? '') : (prefillXuid ?? '')
  )
  const [role,        setRole]        = useState<'operator' | 'member'>(player?.role ?? 'member')
  const [allow1,      setAllow1]      = useState(player?.allowlistInstance1 ?? true)
  const [allow2,      setAllow2]      = useState(player?.allowlistInstance2 ?? true)
  const [looking,     setLooking]     = useState(false)
  const [lookupError, setLookupError] = useState<string | null>(null)

  const firstRef = useRef<HTMLInputElement>(null)
  useEffect(() => { firstRef.current?.focus() }, [])

  async function lookupXuid() {
    if (!gamertag.trim()) return
    setLooking(true)
    setLookupError(null)
    try {
      const result = await api.lookupGamertag(gamertag.trim())
      setXuid(result.xuid ?? '')
      // Normalise gamertag capitalisation to what Xbox returns
      if (!player) setGamertag(result.gamertag)
      cacheXuid(result.gamertag, result.xuid)
    } catch (e) {
      setLookupError((e as Error).message)
    } finally {
      setLooking(false)
    }
  }

  function submit(e: React.FormEvent) {
    e.preventDefault()
    if (!xuid.trim()) return
    onSave({ gamertag: gamertag.trim(), displayName: displayName.trim(), xuid: xuid.trim(), role, allowlistInstance1: allow1, allowlistInstance2: allow2 })
  }

  return (
    <div className="modal-overlay" onClick={e => { if (e.target === e.currentTarget) onClose() }}>
      <div className="modal">
        <div className="modal-header">
          <h2>{player ? 'Edit player' : 'Add player'}</h2>
          <button className="modal-close" onClick={onClose}>×</button>
        </div>

        <form onSubmit={submit}>
          <div className="form-stack">
            <div className="form-group">
              <label>Gamertag</label>
              <div className="flex gap-2">
                <input
                  ref={firstRef}
                  value={gamertag}
                  onChange={e => { setGamertag(e.target.value); setLookupError(null) }}
                  placeholder="e.g. MikeyJ940"
                  required
                  disabled={!!player}
                  style={{ flex: 1 }}
                />
                {!player && (
                  <button
                    type="button"
                    className="btn btn-ghost btn-sm"
                    onClick={lookupXuid}
                    disabled={looking || !gamertag.trim()}
                    title="Look up XUID via Xbox Live"
                    style={{ whiteSpace: 'nowrap' }}
                  >
                    {looking ? <><span className="spinning">◌</span> Looking up…</> : 'Look up XUID'}
                  </button>
                )}
              </div>
              {lookupError && <span className="text-xs" style={{ color: 'var(--red)' }}>⚠ {lookupError}</span>}
            </div>
            <div className="form-group">
              <label>Display name</label>
              <input value={displayName} onChange={e => setDisplayName(e.target.value)}
                     placeholder="e.g. Mikey" required />
            </div>
            <div className="form-group">
              <label>XUID <span style={{ color: 'var(--red)' }}>*</span></label>
              <input value={xuid} onChange={e => setXuid(e.target.value)}
                     placeholder="e.g. 2535449787327445" required />
              <span className="text-xs text-muted">
                {xuid ? '✓ XUID set' : 'Use "Look up XUID" or paste manually'}
              </span>
            </div>
            <div className="form-group">
              <label>Role</label>
              <select value={role} onChange={e => setRole(e.target.value as 'operator' | 'member')}>
                <option value="member">Member</option>
                <option value="operator">Operator</option>
              </select>
            </div>
            <div className="flex gap-4 mt-1">
              <label className="flex items-center gap-2 text-sm" style={{ cursor: 'pointer' }}>
                <input type="checkbox" checked={allow1} onChange={e => setAllow1(e.target.checked)} />
                Allow on Instance 1
              </label>
              <label className="flex items-center gap-2 text-sm" style={{ cursor: 'pointer' }}>
                <input type="checkbox" checked={allow2} onChange={e => setAllow2(e.target.checked)} />
                Allow on Instance 2
              </label>
            </div>
          </div>

          {saveError && <p className="error-msg mt-3">⚠ {saveError}</p>}

          <div className="modal-footer">
            <button type="button" className="btn btn-ghost" onClick={onClose}>Cancel</button>
            <button type="submit" className="btn btn-primary" disabled={saving || !(xuid ?? '').trim()}>
              {saving ? <><span className="spinning">◌</span> Saving…</> : 'Save player'}
            </button>
          </div>
        </form>
      </div>
    </div>
  )
}

// ── Unknown players banner ────────────────────────────────────────────────────

function UnknownPlayersBanner({
  players,
  onAdd,
  onDismiss,
}: {
  players:   UnknownPlayer[]
  onAdd:     (p: UnknownPlayer) => void
  onDismiss: () => void
}) {
  return (
    <div className="card mb-4" style={{ borderColor: 'var(--yellow)', background: 'var(--surface)' }}>
      <div className="flex items-center justify-between mb-2">
        <span style={{ fontWeight: 600, color: 'var(--yellow)' }}>
          {players.length} unknown player{players.length !== 1 ? 's' : ''} detected in server logs
        </span>
        <button className="btn btn-ghost btn-sm" onClick={onDismiss}>Dismiss</button>
      </div>
      <div className="flex flex-col gap-2">
        {players.map(p => (
          <div key={p.xuid} className="flex items-center justify-between" style={{ fontSize: '0.875rem' }}>
            <div>
              <span className="font-mono">{p.gamertag}</span>
              <span className="text-muted" style={{ marginLeft: 8 }}>
                XUID {p.xuid} · seen on Instance{p.seenOn.length > 1 ? 's' : ''} {p.seenOn.join(' & ')}
              </span>
            </div>
            <button className="btn btn-ghost btn-sm" onClick={() => onAdd(p)}>
              + Add
            </button>
          </div>
        ))}
      </div>
    </div>
  )
}

// ── Main page ────────────────────────────────────────────────────────────────

export default function Players() {
  const [players,        setPlayers]        = useState<Player[]>([])
  const [loading,        setLoading]        = useState(true)
  const [error,          setError]          = useState<string | null>(null)
  const [editing,        setEditing]        = useState<Player | null | 'new'>(null)
  const [saving,         setSaving]         = useState(false)
  const [saveError,      setSaveError]      = useState<string | null>(null)
  const [prefillNew,     setPrefillNew]     = useState<{ gamertag: string; xuid: string } | null>(null)
  const [detecting,      setDetecting]      = useState(false)
  const [unknownPlayers, setUnknownPlayers] = useState<UnknownPlayer[] | null>(null)

  const load = useCallback(async () => {
    try {
      const data = await api.getPlayers()
      // Auto-cache any XUIDs returned by the API so toggles work without manual entry
      const cache = getXuidCache()
      let changed = false
      for (const p of data) {
        if (p.xuid && !cache[p.gamertag]) {
          cache[p.gamertag] = p.xuid
          changed = true
        }
      }
      if (changed) localStorage.setItem(XUID_CACHE_KEY, JSON.stringify(cache))
      setPlayers(data)
      setError(null)
    } catch (e) {
      setError((e as Error).message)
    } finally {
      setLoading(false)
    }
  }, [])

  useEffect(() => { load() }, [load])

  async function handleToggle(p: Player, field: 'allowlistInstance1' | 'allowlistInstance2', val: boolean) {
    const xuid = getXuidCache()[p.gamertag]
    if (!xuid) {
      setEditing(p)
      return
    }
    try {
      await api.upsertPlayer({ ...p, xuid, [field]: val })
      setPlayers(prev => prev.map(x => x.gamertag === p.gamertag ? { ...x, [field]: val } : x))
    } catch (e) {
      setError((e as Error).message)
    }
  }

  async function handleSave(np: NewPlayer) {
    setSaving(true)
    setSaveError(null)
    try {
      await api.upsertPlayer(np)
      cacheXuid(np.gamertag, np.xuid)
      setEditing(null)
      setPrefillNew(null)
      load()
    } catch (e) {
      setSaveError((e as Error).message)
    } finally {
      setSaving(false)
    }
  }

  async function detectNewPlayers() {
    setDetecting(true)
    setError(null)
    try {
      const result = await api.detectNewPlayers()
      setUnknownPlayers(result.unknownPlayers)
      if (result.unknownPlayers.length === 0) {
        setError('No unknown players found in the last 7 days of server logs.')
      }
    } catch (e) {
      setError((e as Error).message)
    } finally {
      setDetecting(false)
    }
  }

  function openAddFromDetected(p: UnknownPlayer) {
    setPrefillNew({ gamertag: p.gamertag, xuid: p.xuid })
    setEditing('new')
    setSaveError(null)
  }

  const xuidCache = getXuidCache()

  return (
    <>
      <div className="page-header flex items-center justify-between">
        <div>
          <h1>Players</h1>
          <p>Family player registry — allowlist applies to both instances.</p>
        </div>
        <div className="flex gap-2">
          <button
            className="btn btn-ghost btn-sm"
            onClick={detectNewPlayers}
            disabled={detecting}
            title="Scan server logs for players not in registry"
          >
            {detecting ? <><span className="spinning">◌</span> Scanning…</> : 'Detect new players'}
          </button>
          <button className="btn btn-green btn-sm" onClick={() => { setPrefillNew(null); setEditing('new'); setSaveError(null) }}>
            + Add player
          </button>
        </div>
      </div>

      {unknownPlayers && unknownPlayers.length > 0 && (
        <UnknownPlayersBanner
          players={unknownPlayers}
          onAdd={openAddFromDetected}
          onDismiss={() => setUnknownPlayers(null)}
        />
      )}

      {error && <p className="error-msg mb-4">⚠ {error}</p>}

      {loading && players.length === 0 ? (
        <p className="text-muted text-sm">Loading…</p>
      ) : (
        <div className="card" style={{ padding: 0, overflow: 'hidden' }}>
          <div className="table-wrap">
            <table className="table">
              <thead>
                <tr>
                  <th>Player</th>
                  <th>Gamertag</th>
                  <th>Role</th>
                  <th style={{ textAlign: 'center' }}>Instance 1</th>
                  <th style={{ textAlign: 'center' }}>Instance 2</th>
                  <th></th>
                </tr>
              </thead>
              <tbody>
                {players.map(p => {
                  const hasXuid = !!xuidCache[p.gamertag]
                  return (
                    <tr key={p.gamertag}>
                      <td>
                        <div className="flex items-center gap-3">
                          <div className="avatar" style={{ background: avatarColor(p.gamertag) }}>
                            {initials(p.displayName)}
                          </div>
                          <span style={{ fontWeight: 500 }}>{p.displayName}</span>
                        </div>
                      </td>
                      <td className="text-sm font-mono text-muted">{p.gamertag}</td>
                      <td>
                        {p.role === 'operator'
                          ? <span className="badge badge-yellow">op</span>
                          : <span className="badge badge-gray">member</span>}
                      </td>
                      <td style={{ textAlign: 'center' }}>
                        <label className="toggle toggle-wrap" title={!hasXuid ? 'Open edit to enable toggling' : ''}>
                          <input
                            type="checkbox"
                            checked={p.allowlistInstance1}
                            disabled={!hasXuid}
                            onChange={e => handleToggle(p, 'allowlistInstance1', e.target.checked)}
                          />
                          <span className="toggle-slider" />
                        </label>
                      </td>
                      <td style={{ textAlign: 'center' }}>
                        <label className="toggle toggle-wrap" title={!hasXuid ? 'Open edit to enable toggling' : ''}>
                          <input
                            type="checkbox"
                            checked={p.allowlistInstance2}
                            disabled={!hasXuid}
                            onChange={e => handleToggle(p, 'allowlistInstance2', e.target.checked)}
                          />
                          <span className="toggle-slider" />
                        </label>
                      </td>
                      <td>
                        <button
                          className="btn btn-ghost btn-sm"
                          onClick={() => { setEditing(p); setSaveError(null) }}
                        >
                          Edit
                        </button>
                      </td>
                    </tr>
                  )
                })}
              </tbody>
            </table>
          </div>
        </div>
      )}

      {!loading && players.length === 0 && (
        <p className="text-muted text-sm mt-4">No players found.</p>
      )}

      <p className="text-xs text-muted mt-3">
        Allowlist toggles require XUID (cached after first edit or lookup).
        Saving a player triggers an automatic allowlist sync to both instances.
      </p>

      {editing !== null && (
        <PlayerModal
          player={editing === 'new' ? undefined : editing}
          prefillGamertag={editing === 'new' ? prefillNew?.gamertag : undefined}
          prefillXuid={editing === 'new' ? prefillNew?.xuid : undefined}
          onSave={handleSave}
          onClose={() => { setEditing(null); setPrefillNew(null) }}
          saving={saving}
          saveError={saveError}
        />
      )}
    </>
  )
}
